import { HUD, MAP, MAP_FRAME } from '../config/tuning';
import { THREATS } from '../config/threats';
import type { Mission } from '../mission/schema';
import type { Terrain } from '../world/Terrain';

/**
 * The top-down theatre map, used by the briefing and the debrief (SPEC §3.14).
 *
 * SPEC is unusually firm about this one: threat rings must be drawn *as rings on a map*, "not as a
 * list, because the player's job is spatial". A table of ranges tells you a site reaches 12 km; a
 * ring tells you whether your route goes through it, which is the actual question.
 *
 * Search and engagement radii are drawn **separately**, also per spec, because they mean different
 * things: the outer ring is where you are seen, the inner is where you are shot at, and the gap
 * between them is the part of the route you can fly knowingly.
 *
 * Canvas2D on its own element, sized in CSS pixels; world metres map linearly to pixels.
 */

export interface MapView {
  /** World metres across the full map width. */
  readonly spanM: number;
  readonly centreXM: number;
  readonly centreZM: number;
}

export function defaultView(): MapView {
  return { spanM: MAP.defaultSpanM, centreXM: MAP.centreXM, centreZM: MAP.centreZM };
}

/**
 * Frame the map on a mission rather than on the theatre.
 *
 * Always showing all 24 km makes the briefing useless for the missions that matter: a SHORAD's
 * engagement radius is 12 km, so at altitude a single site covers the entire theatre and the map
 * becomes one flat red disc. That is *true* — terrain is the only real cover, which is the whole
 * lesson of mission 5 — but a picture that says "everywhere is lethal" is not a planning surface.
 *
 * Framing on the mission's own extents restores the thing SPEC §3.14 wants the map to show: where
 * the route runs relative to the rings, and which parts of it are inside which.
 */
export function frameFor(mission: Mission): MapView {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minZ = Number.POSITIVE_INFINITY;
  let maxZ = Number.NEGATIVE_INFINITY;

  const include = (xM: number, zM: number): void => {
    if (xM < minX) minX = xM;
    if (xM > maxX) maxX = xM;
    if (zM < minZ) minZ = zM;
    if (zM > maxZ) maxZ = zM;
  };

  include(mission.launch.position[0], mission.launch.position[2]);
  for (let i = 0; i < mission.route.length; i += 1) {
    include(mission.route[i].position[0], mission.route[i].position[2]);
  }
  for (let i = 0; i < mission.targets.length; i += 1) {
    include(mission.targets[i].position[0], mission.targets[i].position[2]);
  }
  // Threat *positions* are included, but not their rings: a 28 km search radius would zoom the
  // map back out to the theatre and undo the point of framing.
  for (let i = 0; i < mission.threats.length; i += 1) {
    if (!mission.threats[i].active) continue;
    include(mission.threats[i].position[0], mission.threats[i].position[2]);
  }

  if (!Number.isFinite(minX)) return defaultView();

  const spanM = Math.min(
    MAP.defaultSpanM,
    Math.max(MAP_FRAME.minSpanM, Math.max(maxX - minX, maxZ - minZ) * MAP_FRAME.framePadding),
  );
  return { spanM, centreXM: (minX + maxX) * 0.5, centreZM: (minZ + maxZ) * 0.5 };
}

export class TheatreMap {
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private view: MapView = defaultView();
  private terrainShade: ImageData | null = null;
  /** Label boxes already placed this draw, so the next one can avoid them. */
  private readonly placedLabels: { x: number; y: number; w: number; h: number }[] = [];

  constructor(widthPx = MAP.sizePx, heightPx = MAP.sizePx) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = widthPx;
    this.canvas.height = heightPx;
    this.canvas.className = 'vz-map';
    const ctx = this.canvas.getContext('2d');
    if (ctx === null) throw new Error('TheatreMap: 2D context unavailable');
    this.ctx = ctx;
  }

  setView(view: MapView): void {
    this.view = view;
  }

  /** World metres → canvas pixels. */
  private toPx(xM: number, zM: number): { x: number; y: number } {
    const scale = this.canvas.width / this.view.spanM;
    return {
      x: this.canvas.width * 0.5 + (xM - this.view.centreXM) * scale,
      // North is up: +z runs south, so the sign flips.
      y: this.canvas.height * 0.5 - (zM - this.view.centreZM) * scale,
    };
  }

  private metresToPx(m: number): number {
    return (m * this.canvas.width) / this.view.spanM;
  }

  /**
   * Shade the terrain once and cache it.
   *
   * Sampling the heightfield per pixel is a few hundred thousand samples; doing it on every
   * redraw would make the map stutter under a scrub. It only changes when the view does.
   */
  shadeTerrain(terrain: Terrain): void {
    const { width, height } = this.canvas;
    const image = this.ctx.createImageData(width, height);
    const data = image.data;
    const scale = this.view.spanM / width;

    for (let py = 0; py < height; py += 1) {
      const zM = this.view.centreZM + (height * 0.5 - py) * scale;
      for (let px = 0; px < width; px += 1) {
        const xM = this.view.centreXM + (px - width * 0.5) * scale;
        const h = terrain.surfaceHeightAt(xM, zM);
        const i = (py * width + px) * 4;
        if (h <= MAP.seaLevelM) {
          // Water: flat and dark, so the coastline reads as a hard edge.
          data[i] = 10;
          data[i + 1] = 22;
          data[i + 2] = 30;
        } else {
          const t = Math.min(1, h / MAP.shadeCeilingM);
          data[i] = 24 + t * 70;
          data[i + 1] = 34 + t * 74;
          data[i + 2] = 30 + t * 60;
        }
        data[i + 3] = 255;
      }
    }
    this.terrainShade = image;
  }

  /** Draw the briefing picture: terrain, threat rings, route, targets. */
  drawBriefing(mission: Mission): void {
    const ctx = this.ctx;
    this.clear();

    // Threat rings first, under everything: they are the terrain of the problem.
    for (let i = 0; i < mission.threats.length; i += 1) {
      const threat = mission.threats[i];
      if (!threat.active) continue; // Unknown until it switches on; the brief cannot show it.
      const params = THREATS[threat.type];
      const at = this.toPx(threat.position[0], threat.position[2]);

      const searchM = threat.overrides?.searchRangeM ?? params.searchRangeM;
      const engageM = threat.overrides?.maxEngageM ?? params.maxEngageM;

      // Search ring: where you are seen.
      ctx.strokeStyle = MAP.searchRingColour;
      ctx.setLineDash([4, 5]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(at.x, at.y, this.metresToPx(searchM), 0, Math.PI * 2);
      ctx.stroke();

      // Engagement ring: where you are shot at. Solid, and filled faintly — the gap between the
      // two rings is the part of the map that is knowingly survivable.
      ctx.setLineDash([]);
      ctx.strokeStyle = MAP.engageRingColour;
      ctx.fillStyle = MAP.engageFillColour;
      ctx.beginPath();
      ctx.arc(at.x, at.y, this.metresToPx(engageM), 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = MAP.engageRingColour;
      ctx.fillRect(at.x - 3, at.y - 3, 6, 6);
      this.label(threat.id, at.x + 6, at.y - 6, MAP.engageRingColour);
    }

    // Targets first so they keep the position that means something; route furniture moves.
    this.drawTargets(mission);
    this.drawRoute(mission);
    this.drawLaunch(mission);
  }

  private drawRoute(mission: Mission): void {
    const ctx = this.ctx;
    ctx.setLineDash([]);
    ctx.strokeStyle = MAP.routeColour;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    const launch = this.toPx(mission.launch.position[0], mission.launch.position[2]);
    ctx.moveTo(launch.x, launch.y);
    for (let i = 0; i < mission.route.length; i += 1) {
      const at = this.toPx(mission.route[i].position[0], mission.route[i].position[2]);
      ctx.lineTo(at.x, at.y);
    }
    ctx.stroke();

    ctx.font = `${MAP.labelPx}px ${HUD.fontFamily}`;
    for (let i = 0; i < mission.route.length; i += 1) {
      const waypoint = mission.route[i];
      const at = this.toPx(waypoint.position[0], waypoint.position[2]);
      ctx.strokeStyle = MAP.routeColour;
      ctx.beginPath();
      ctx.arc(at.x, at.y, 4, 0, Math.PI * 2);
      ctx.stroke();
      this.label(waypoint.label, at.x + 7, at.y + 3, MAP.routeColour);
    }
  }

  private drawTargets(mission: Mission): void {
    const ctx = this.ctx;
    ctx.font = `${MAP.labelPx}px ${HUD.fontFamily}`;
    for (let i = 0; i < mission.targets.length; i += 1) {
      const target = mission.targets[i];
      const at = this.toPx(target.position[0], target.position[2]);
      ctx.strokeStyle = MAP.targetColour;
      ctx.lineWidth = 1.5;
      // A diamond, matching the HUD's target glyph so the two screens agree.
      ctx.beginPath();
      ctx.moveTo(at.x, at.y - 6);
      ctx.lineTo(at.x + 6, at.y);
      ctx.lineTo(at.x, at.y + 6);
      ctx.lineTo(at.x - 6, at.y);
      ctx.closePath();
      ctx.stroke();
      this.label(target.label, at.x + 9, at.y + 15, MAP.targetColour);
    }
  }

  private drawLaunch(mission: Mission): void {
    const ctx = this.ctx;
    const at = this.toPx(mission.launch.position[0], mission.launch.position[2]);
    ctx.strokeStyle = MAP.routeColour;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(at.x - 6, at.y + 5);
    ctx.lineTo(at.x, at.y - 7);
    ctx.lineTo(at.x + 6, at.y + 5);
    ctx.closePath();
    ctx.stroke();
  }

  /** Overlay the flown track, for the debrief. `track` is flat `x, z` pairs in metres. */
  drawTrack(track: Float32Array): void {
    if (track.length < 4) return;
    const ctx = this.ctx;
    ctx.setLineDash([]);
    ctx.strokeStyle = MAP.trackColour;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    const first = this.toPx(track[0], track[1]);
    ctx.moveTo(first.x, first.y);
    for (let i = 2; i < track.length; i += 2) {
      const at = this.toPx(track[i], track[i + 1]);
      ctx.lineTo(at.x, at.y);
    }
    ctx.stroke();

    const endIndex = track.length - 2;
    const end = this.toPx(track[endIndex], track[endIndex + 1]);
    ctx.fillStyle = MAP.trackEndColour;
    ctx.beginPath();
    ctx.arc(end.x, end.y, 4, 0, Math.PI * 2);
    ctx.fill();
  }

  /**
   * Draw a label, nudged off anything already placed.
   *
   * A route ends at its target, so the last waypoint's label and the target's label land within a
   * few pixels of each other on every mission — `VALLEY MOUTH` over `SAM LAUNCHER` over
   * `LAUNCHER`, drawn as one smear. Candidates are tried in order and the first clear one wins,
   * so the common case costs a single rectangle test and nothing moves unless it has to.
   */
  private label(text: string, x: number, y: number, colour: string): void {
    const ctx = this.ctx;
    ctx.font = `${MAP.labelPx}px ${HUD.fontFamily}`;
    const w = ctx.measureText(text).width;
    const h = MAP.labelPx * 1.25;

    // Right of the mark first, then below, above, and left — the order a chart would use.
    const candidates = [
      { x, y },
      { x, y: y + h * 1.15 },
      { x, y: y - h * 1.15 },
      { x, y: y + h * 2.3 },
      { x: x - w - MAP.labelPx * 1.6, y },
      { x, y: y - h * 2.3 },
    ];

    let chosen = candidates[0];
    for (let i = 0; i < candidates.length; i += 1) {
      const c = candidates[i];
      if (!this.labelCollides(c.x, c.y, w, h)) {
        chosen = c;
        break;
      }
    }

    this.placedLabels.push({ x: chosen.x, y: chosen.y - h, w, h });
    ctx.fillStyle = colour;
    ctx.fillText(text, chosen.x, chosen.y);
  }

  private labelCollides(x: number, y: number, w: number, h: number): boolean {
    const top = y - h;
    for (let i = 0; i < this.placedLabels.length; i += 1) {
      const other = this.placedLabels[i];
      if (x > other.x + other.w || x + w < other.x) continue;
      if (top > other.y + other.h || top + h < other.y) continue;
      return true;
    }
    return false;
  }

  private clear(): void {
    this.placedLabels.length = 0;
    const ctx = this.ctx;
    if (this.terrainShade !== null) {
      ctx.putImageData(this.terrainShade, 0, 0);
    } else {
      ctx.fillStyle = '#0a1016';
      ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }
}
